import {LocalizedHtml, useI18n} from '@udemy-v2/i18n';
import {Icon} from '@udemy-v2/icons';
import {Ribbon} from '@udemy-v2/react-notice-components';

import styles from './google-certificate-ribbon.module.scss';
import {useMemo} from 'react';
import {CareerAcceleratorsByCourseIds} from '@udemy-v2/career-accelerators';
import {getGoogleCareerPathUrl} from '@/features/course-landing-page/utils/google';

/**
 * Props for the GoogleCertificateRibbon component
 */
export interface GoogleCertificateRibbonProps {
    courseCareerAccelerator: CareerAcceleratorsByCourseIds | undefined;
    firstCourseId: string;
}

/**
 * Component that displays a ribbon indicating the course is part of a Google Certificate program.
 * Shows the current course number out of total courses and provides a link to the certificate page.
 */
export function GoogleCertificateRibbon({
    courseCareerAccelerator,
    firstCourseId,
}: GoogleCertificateRibbonProps) {
    const {gettext, interpolate} = useI18n();
    const ribbonData = useMemo(() => {
        if (!courseCareerAccelerator) {
            return null;
        }

        // Type assertion needed because CareerAcceleratorsByCourseIds type may not include all properties
        const accelerator = courseCareerAccelerator as unknown as {
            curriculum: {sections: Array<{items: Array<{id: string}>}>};
            career: {name: string};
            slug: string;
            stats?: {numberOfCourses?: number};
        };

        const firstSection = accelerator.curriculum.sections[0];
        if (!firstSection) {
            return null;
        }

        const totalCourses = accelerator.stats?.numberOfCourses ?? 0;
        const currentCoursePosition =
            firstSection.items.findIndex((item: {id: string}) => item.id === firstCourseId) + 1;
        const coursesInCurrentAccelerator = firstSection.items.length;

        return {
            careerName: accelerator.career.name,
            careerSlug: accelerator.slug,
            totalCourses,
            currentCoursePosition,
            coursesInCurrentAccelerator,
        };
    }, [courseCareerAccelerator, firstCourseId]);

    const localizedHtml = ribbonData ? (
        <LocalizedHtml
            html={interpolate(
                gettext(
                    'Course %(currentCourse)s/%(totalCourses)s of <a class="certificate-link">%(careerName)s</a>',
                ),
                {
                    currentCourse: ribbonData.currentCoursePosition,
                    totalCourses: ribbonData.totalCourses,
                    careerName: ribbonData.careerName,
                },
                true,
            )}
            interpolate={{
                'certificate-link': (
                    <a
                        href={getGoogleCareerPathUrl(ribbonData.careerSlug)}
                        className={styles['google-certificate-link']}
                    />
                ),
            }}
        />
    ) : (
        <LocalizedHtml
            html={interpolate(
                gettext('Included in <a class="certificate-link">Learn AI with Google</a>'),
                true,
            )}
            interpolate={{
                'certificate-link': (
                    <a
                        href={`/partner/google/learn-ai/`}
                        className={styles['google-certificate-link']}
                    />
                ),
            }}
        />
    );

    return (
        <div className={styles['google-certificate-ribbon']}>
            <Ribbon color="purple-150" className={styles['google-certificate-ribbon-inner']}>
                <Icon
                    glyph="sparkles"
                    size="xsmall"
                    label={false}
                    color="inherit"
                    className={styles['icon-google']}
                />
                {localizedHtml}
            </Ribbon>
        </div>
    );
}
